/**
 * Font Debugger Utility
 * Helps debug custom font loading issues
 */

/**
 * Debug a specific custom font
 * Shows all @font-face rules and checks if font is loaded
 */
export const debugCustomFont = (fontName) => {
    console.group(`🔍 Font Debug: "${fontName}"`);
    
    // 1. Check if CSS is injected
    const styleElement = document.querySelector(`[data-custom-font="${fontName}"]`);
    if (styleElement) {
        console.log('✅ CSS Style Element Found');
        console.log('CSS Content:', styleElement.textContent);
    } else {
        console.error('❌ CSS Style Element NOT Found');
    }
    
    // 2. Check if font is in document.fonts
    const fontFaces = Array.from(document.fonts).filter(f => f.family === fontName || f.family === `"${fontName}"` || f.family === `'${fontName}'`);
    if (fontFaces.length > 0) {
        console.log(`✅ Found ${fontFaces.length} FontFace(s) in document.fonts:`, fontFaces.map(f => ({
            family: f.family,
            weight: f.weight,
            style: f.style,
            status: f.status
        })));
    } else {
        console.warn('⚠️ Font NOT found in document.fonts');
    }
    
    // 3. Check if font is available (can be used)
    try {
        const isAvailable = document.fonts.check(`16px "${fontName}"`);
        console.log(`Font Available Check (16px): ${isAvailable ? '✅' : '❌'}`);
        
        // Try different weights
        [100, 300, 400, 500, 600, 700, 800, 900].forEach(weight => {
            const isWeightAvailable = document.fonts.check(`${weight} 16px "${fontName}"`);
            if (isWeightAvailable) {
                console.log(`  ✅ Weight ${weight} available`);
            }
        });
    } catch (e) {
        console.error('❌ Error checking font availability:', e);
    }
    
    // 4. List all @font-face rules in stylesheets
    console.log('📋 All @font-face rules with this font:');
    let foundRules = 0;
    for (const sheet of document.styleSheets) {
        try {
            const rules = sheet.cssRules || sheet.rules;
            for (const rule of rules) {
                if (rule instanceof CSSFontFaceRule) {
                    const family = rule.style.fontFamily.replace(/['"]/g, '');
                    if (family === fontName) {
                        foundRules++;
                        console.log(`  Rule ${foundRules}:`, {
                            family: rule.style.fontFamily,
                            src: rule.style.src,
                            weight: rule.style.fontWeight,
                            style: rule.style.fontStyle,
                            display: rule.style.fontDisplay
                        });
                    }
                }
            }
        } catch (e) {
            // CORS errors are expected for external stylesheets
        }
    }
    
    if (foundRules === 0) {
        console.warn('⚠️ No @font-face rules found in stylesheets');
    }
    
    // 5. Test render the font
    console.log('🎨 Creating test element...');
    const testDiv = document.createElement('div');
    testDiv.style.fontFamily = `"${fontName}", Arial, sans-serif`;
    testDiv.style.fontSize = '24px';
    testDiv.style.position = 'fixed';
    testDiv.style.top = '10px';
    testDiv.style.right = '10px';
    testDiv.style.background = 'rgba(255, 255, 255, 0.95)';
    testDiv.style.padding = '10px';
    testDiv.style.border = '2px solid #667eea';
    testDiv.style.borderRadius = '8px';
    testDiv.style.zIndex = '999999';
    testDiv.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
    testDiv.innerHTML = `
        <div style="font-weight: 300;">Light (300): ${fontName}</div>
        <div style="font-weight: 400;">Regular (400): ${fontName}</div>
        <div style="font-weight: 600;">SemiBold (600): ${fontName}</div>
        <div style="font-weight: 700;">Bold (700): ${fontName}</div>
        <button onclick="this.parentElement.remove()" style="margin-top: 10px; padding: 5px 10px; cursor: pointer;">Close</button>
    `;
    document.body.appendChild(testDiv);
    console.log('✅ Test element added to page (top-right corner)');
    
    // 6. Get computed font for test element
    setTimeout(() => {
        const firstChild = testDiv.firstElementChild;
        const computedFont = window.getComputedStyle(firstChild).fontFamily;
        console.log('🖥️ Computed fontFamily:', computedFont);
        
        if (!computedFont.includes(fontName)) {
            console.error(`❌ PROBLEM: Browser is using "${computedFont}" instead of "${fontName}"!`);
            console.log('💡 This usually means:');
            console.log('   1. Font file failed to load (CORS issue?)');
            console.log('   2. Font file is corrupted');
            console.log('   3. Font format not supported');
            console.log('   4. Font-family name mismatch');
        } else {
            console.log('✅ Browser is correctly using the custom font');
        }
    }, 1000);
    
    console.groupEnd();
};

/**
 * Debug all custom fonts
 */
export const debugAllCustomFonts = () => {
    const customFontElements = document.querySelectorAll('[data-custom-font]');
    console.log(`📦 Found ${customFontElements.length} custom font style elements`);
    
    customFontElements.forEach(el => {
        const fontName = el.getAttribute('data-custom-font');
        debugCustomFont(fontName);
    });
};

/**
 * Test if a font URL is accessible
 */
export const testFontURL = async (fontUrl) => {
    console.log(`🔗 Testing font URL: ${fontUrl}`);
    
    try {
        const response = await fetch(fontUrl, { method: 'HEAD' });
        if (response.ok) {
            console.log('✅ Font URL is accessible');
            console.log('   Status:', response.status);
            console.log('   Content-Type:', response.headers.get('Content-Type'));
            console.log('   Content-Length:', response.headers.get('Content-Length'));
            
            // Check CORS headers
            const corsHeader = response.headers.get('Access-Control-Allow-Origin');
            if (corsHeader) {
                console.log('✅ CORS header present:', corsHeader);
            } else {
                console.warn('⚠️ No CORS header - might cause issues');
            }
            
            return true;
        } else {
            console.error('❌ Font URL returned error:', response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error('❌ Failed to fetch font URL:', error);
        return false;
    }
};

/**
 * Show font loading summary
 */
export const fontLoadingSummary = () => {
    console.group('📊 Font Loading Summary');
    
    // All fonts in document
    const allFonts = Array.from(document.fonts);
    console.log(`Total fonts in document.fonts: ${allFonts.length}`);
    
    // Custom fonts
    const customFontElements = document.querySelectorAll('[data-custom-font]');
    console.log(`Custom font style elements: ${customFontElements.length}`);
    
    // Group by status
    const fontsByStatus = allFonts.reduce((acc, font) => {
        acc[font.status] = (acc[font.status] || 0) + 1;
        return acc;
    }, {});
    console.log('Fonts by status:', fontsByStatus);
    
    // List unique font families
    const fontFamilies = [...new Set(allFonts.map(f => f.family))];
    console.log(`Unique font families: ${fontFamilies.length}`, fontFamilies);
    
    console.groupEnd();
};

// Make functions available globally for console debugging
if (typeof window !== 'undefined') {
    window.__fontDebug = {
        debugFont: debugCustomFont,
        debugAll: debugAllCustomFonts,
        testURL: testFontURL,
        summary: fontLoadingSummary
    };
}
